import { createSignal, type Component } from "solid-js";
import { PanelLayout, type SubTab, type PanelStatus } from "@/shared/panel-base";
import { DataTable, type Column } from "@/shared/data-table";
import { createToolQuery } from "@/shared/query";
import { useToolClient } from "@/shared/tool-client";
import { useConfirm } from "@/shared/confirm-dialog";
import { useWs } from "@/shared/ws";
import { registerPanel } from "@/panel-registry";

interface ClusterNode {
  Name?: string;
  State?: string;
  NodeWeight?: number;
  DynamicWeight?: number;
}

interface ClusterResource {
  Name?: string;
  State?: string;
  OwnerNode?: string;
  ResourceType?: string;
  OwnerGroup?: string;
}

interface ClusterGroup {
  Name?: string;
  State?: string;
  OwnerNode?: string;
}

interface ListResult<T> {
  rows: T[];
}

const ClusterPanel: Component = () => {
  const ws = useWs();
  const confirm = useConfirm();
  const tools = useToolClient();
  const [actionStatus, setActionStatus] = createSignal<PanelStatus | null>(null);

  const enabled = () => ws.state.nativeAgentOnline && ws.state.supportApproved;

  const nodesQuery = createToolQuery<ListResult<ClusterNode>>(() => ({
    tool: "cluster_nodes_list",
    params: {},
    enabled: enabled(),
    refetchInterval: 0,
  }));

  const resourcesQuery = createToolQuery<ListResult<ClusterResource>>(() => ({
    tool: "cluster_resources_list",
    params: {},
    enabled: enabled(),
    refetchInterval: 0,
  }));

  const groupsQuery = createToolQuery<ListResult<ClusterGroup>>(() => ({
    tool: "cluster_groups_list",
    params: {},
    enabled: enabled(),
    refetchInterval: 0,
  }));

  async function runNodeAction(
    nodeName: string,
    actionId: "pause" | "resume",
    label: string,
    level: "info" | "warn" | "danger",
  ): Promise<void> {
    if (!nodeName) return;
    const ok = await confirm.confirm({
      title: `${label} - ${nodeName}`,
      body: `Confirmer l'action cluster "${label}" sur le noeud ${nodeName} ?`,
      level,
      confirmLabel: label,
      entityName: level === "danger" ? nodeName : "",
    });
    if (!ok) return;
    setActionStatus({ text: `Execution ${label} sur ${nodeName}...`, level: "info" });
    try {
      await tools.call(
        "cluster_node_action",
        { nodeName, action: actionId },
        { userConfirmedAt: new Date().toISOString() },
      );
      setActionStatus({ text: `Action ${label} sur ${nodeName} reussie.`, level: "info" });
      await nodesQuery.refetch();
    } catch (err) {
      setActionStatus({
        text: `Erreur action: ${err instanceof Error ? err.message : String(err)}`,
        level: "danger",
      });
    }
  }

  async function runResourceAction(
    resourceName: string,
    actionId: "online" | "offline" | "move",
    label: string,
    level: "info" | "warn" | "danger",
  ): Promise<void> {
    if (!resourceName) return;
    let targetNode: string | undefined;
    if (actionId === "move") {
      const input = window.prompt("Noeud cible pour le deplacement :", "");
      if (input === null) return;
      targetNode = input.trim();
      if (!targetNode) {
        setActionStatus({ text: "Noeud cible requis.", level: "warn" });
        return;
      }
    }
    const ok = await confirm.confirm({
      title: `${label} - ${resourceName}`,
      body: `Confirmer l'action cluster "${label}" sur la ressource ${resourceName}${targetNode ? ` vers ${targetNode}` : ""} ?`,
      level,
      confirmLabel: label,
      entityName: level === "danger" ? resourceName : "",
    });
    if (!ok) return;
    setActionStatus({ text: `Execution ${label} sur ${resourceName}...`, level: "info" });
    try {
      const params: Record<string, unknown> = { resourceName, action: actionId };
      if (targetNode) params.targetNode = targetNode;
      await tools.call("cluster_resource_action", params, {
        userConfirmedAt: new Date().toISOString(),
      });
      setActionStatus({ text: `Action ${label} sur ${resourceName} reussie.`, level: "info" });
      await resourcesQuery.refetch();
    } catch (err) {
      setActionStatus({
        text: `Erreur action: ${err instanceof Error ? err.message : String(err)}`,
        level: "danger",
      });
    }
  }

  const nodeColumns: Column<ClusterNode>[] = [
    { accessorKey: "Name", header: "Name" },
    { accessorKey: "State", header: "State" },
    { accessorKey: "NodeWeight", header: "NodeWeight" },
    { accessorKey: "DynamicWeight", header: "DynamicWeight" },
  ];

  const resourceColumns: Column<ClusterResource>[] = [
    { accessorKey: "Name", header: "Name" },
    { accessorKey: "State", header: "State" },
    { accessorKey: "OwnerNode", header: "OwnerNode" },
    { accessorKey: "ResourceType", header: "ResourceType" },
    { accessorKey: "OwnerGroup", header: "OwnerGroup" },
  ];

  const groupColumns: Column<ClusterGroup>[] = [
    { accessorKey: "Name", header: "Name" },
    { accessorKey: "State", header: "State" },
    { accessorKey: "OwnerNode", header: "OwnerNode" },
  ];

  const subtabs: SubTab[] = [
    {
      id: "nodes",
      label: "Nodes",
      render: () => (
        <>
          <div class="panel-toolbar panel-toolbar--filter">
            <button type="button" class="btn btn--xs btn--info" onClick={() => nodesQuery.refetch()}>
              Rafraichir
            </button>
          </div>
          <DataTable<ClusterNode>
            rows={nodesQuery.data?.rows ?? []}
            columns={nodeColumns}
            rowActions={(r) => {
              const stateLower = String(r.State ?? "").toLowerCase();
              const name = r.Name ?? "";
              return (
                <>
                  <button
                    type="button"
                    class="btn btn--xs btn--warn"
                    disabled={stateLower === "paused"}
                    onClick={() => runNodeAction(name, "pause", "Pause", "warn")}
                  >
                    Pause
                  </button>
                  <button
                    type="button"
                    class="btn btn--xs btn--ghost"
                    disabled={stateLower === "up"}
                    onClick={() => runNodeAction(name, "resume", "Resume", "info")}
                  >
                    Resume
                  </button>
                </>
              );
            }}
          />
        </>
      ),
    },
    {
      id: "resources",
      label: "Resources",
      render: () => (
        <>
          <div class="panel-toolbar panel-toolbar--filter">
            <button type="button" class="btn btn--xs btn--info" onClick={() => resourcesQuery.refetch()}>
              Rafraichir
            </button>
          </div>
          <DataTable<ClusterResource>
            rows={resourcesQuery.data?.rows ?? []}
            columns={resourceColumns}
            rowActions={(r) => {
              const stateLower = String(r.State ?? "").toLowerCase();
              const name = r.Name ?? "";
              return (
                <>
                  <button
                    type="button"
                    class="btn btn--xs btn--ghost"
                    disabled={stateLower === "online"}
                    onClick={() => runResourceAction(name, "online", "Online", "info")}
                  >
                    Online
                  </button>
                  <button
                    type="button"
                    class="btn btn--xs btn--warn"
                    onClick={() => runResourceAction(name, "move", "Move", "warn")}
                  >
                    Move
                  </button>
                  <button
                    type="button"
                    class="btn btn--xs btn--danger"
                    disabled={stateLower === "offline"}
                    onClick={() => runResourceAction(name, "offline", "Offline", "danger")}
                  >
                    Offline
                  </button>
                </>
              );
            }}
          />
        </>
      ),
    },
    {
      id: "groups",
      label: "Groups",
      render: () => (
        <>
          <div class="panel-toolbar panel-toolbar--filter">
            <button type="button" class="btn btn--xs btn--info" onClick={() => groupsQuery.refetch()}>
              Rafraichir
            </button>
          </div>
          <DataTable<ClusterGroup> rows={groupsQuery.data?.rows ?? []} columns={groupColumns} />
        </>
      ),
    },
  ];

  const status = (): PanelStatus | undefined => {
    if (actionStatus()) return actionStatus() ?? undefined;
    if (!ws.state.nativeAgentOnline) return { text: "Module natif requis.", level: "warn" };
    if (!ws.state.supportApproved) return { text: "Validation client requise.", level: "warn" };
    if (nodesQuery.isFetching || resourcesQuery.isFetching || groupsQuery.isFetching)
      return { text: "Chargement...", level: "info" };
    const err = nodesQuery.error || resourcesQuery.error || groupsQuery.error;
    if (err)
      return {
        text: `Erreur: ${err instanceof Error ? err.message : String(err)}`,
        level: "danger",
      };
    return undefined;
  };

  return <PanelLayout title="Failover Cluster" subtabs={subtabs} status={status()} />;
};

registerPanel("cluster", ClusterPanel);
export default ClusterPanel;
